import { Handling } from '../../../models/Vehicle'
import { FineTuningCache } from '../FineTuning.cache'
import { FineTuningSettingsProfileCache } from '../FineTuning.types'
import { PerformaceValues } from './FineTuningCharacteristics.types'

export class FineTuningCaracteristicsService {

	private static instance: FineTuningCaracteristicsService | null = null;
	private handling: Handling | null = null;
	private performance: PerformaceValues | null = null;

	private Constants = {
		DOWNPIPE_MIN_MM: 51,
		DOWNPIPE_MAX_MM: 102,
		F_INTIAL_DRIVE_FORCE_MIN: 0.1,
		F_INTIAL_DRIVE_FORCE_MAX: 0.6,

		ACCELERATION_RESPONSE_FASTEST_MS: 80,
		ACCELERATION_RESPONSE_SLOWEST_MS: 350,

		F_INERTIA_MIN: 0.01,
		F_INERTIA_MAX: 2.0,

		DOWNPIPE_MAX_SWING: 0.30,
	}

	private baseParameters: { power: number; torque: number } | null = null;

	private constructor() { }

	public static getInstance(): FineTuningCaracteristicsService {
		if (!FineTuningCaracteristicsService.instance) {
			FineTuningCaracteristicsService.instance = new FineTuningCaracteristicsService()
		}
		return FineTuningCaracteristicsService.instance
	}

	public setHandling(handling: Handling) {
		this.handling = handling
	}

	public getHandling() {
		return this.handling
	}

	public setBaseParameters(parameters: { power: number; torque: number }) {
		this.baseParameters = parameters
	}

	public calculatePerformaceValues = (profileName: string): PerformaceValues => {
		const cache = FineTuningCache.getInstance()
		const userSettings = cache.getCache()
		const profile = cache.getProfileByName(profileName, userSettings)
		if (!this.handling || !userSettings || !profile) return this.performance ?? {
			TOP_SPEED: 0,
			DOWN_FORCE: 0,
			CASTOR: 0,
			PERFORMANCE: 0,
			ACCELERATION_RESPONSIVENESS: 0,
			FRONT_CAMBER: 0,
			REAR_CAMBER: 0,
			FRONT_TOE: 0,
			REAR_TOE: 0,
			CLEARANCE_CHANGE: 0,
			STEERING_LOCK: 0,
			SLIP_ANGLE: 0,
			FRONT_TRACK_WIDTH: 0,
			REAR_TRACK_WIDTH: 0,
			TORQUE: 0,
		}
		this.performance = {
			TOP_SPEED: this.calculateTopSpeed(profile),
			DOWN_FORCE: this.calculateDownForce(profile),
			CASTOR: this.calculateCastor(profile),
			PERFORMANCE: this.calculatePerformance(profile),
			ACCELERATION_RESPONSIVENESS: this.calculateAccelerationResponsiveness(profile),
			FRONT_CAMBER: this.calculateFrontCamber(profile),
			REAR_CAMBER: this.calculateRearCamber(profile),
			FRONT_TOE: this.calculateFrontToe(profile),
			REAR_TOE: this.calculateRearToe(profile),
			CLEARANCE_CHANGE: this.calculateClearanceChange(profile),
			STEERING_LOCK: this.calculateSteeringLock(profile),
			SLIP_ANGLE: this.calculateSlipAngle(profile),
			FRONT_TRACK_WIDTH: this.calculateFrontTrackWidth(profile),
			REAR_TRACK_WIDTH: this.calculateRearTrackWidth(profile),
			TORQUE: this.calculateTorque(profile),
		}
		console.log(this.performance)
		return this.performance
	}

	private calculateFrontTrackWidth = (settings: FineTuningSettingsProfileCache): number => {
		if (!settings) return 0
		return settings.frontTrackWidth ?? 0
	}

	private calculateRearTrackWidth = (settings: FineTuningSettingsProfileCache): number => {
		if (!settings) return 0
		return settings.rearTrackWidth ?? 0
	}

	private calculateSteeringLock = (settings: FineTuningSettingsProfileCache): number => {
		if (!settings) return 0
		return settings.steeringLock ?? 0
	}

	private calculateSlipAngle = (settings: FineTuningSettingsProfileCache): number => {
		if (!settings) return 0
		return settings.slipAngle ?? 0
	}

	private calculateFrontCamber = (settings: FineTuningSettingsProfileCache): number => {
		if (!settings) return 0
		return settings.frontCamber ?? 0
	}

	private calculateClearanceChange = (settings: FineTuningSettingsProfileCache): number => {
		if (!settings) return 0
		return settings.clearanceChange ?? 0
	}

	private calculateRearCamber = (settings: FineTuningSettingsProfileCache): number => {
		if (!settings) return 0
		return settings.rearCamber ?? 0
	}

	private calculateRearToe = (settings: FineTuningSettingsProfileCache): number => {
		if (!settings) return 0
		return settings.rearToe ?? 0
	}

	private calculateFrontToe = (settings: FineTuningSettingsProfileCache): number => {
		if (!settings) return 0
		return settings.frontToe ?? 0
	}

	private calculateTopSpeed = (userSettings: FineTuningSettingsProfileCache): number => {
		if (!this.handling || !userSettings) return 0
		const theoreticalMaxKph = (this.handling.finitialDriveMaxFlatVel * 1.2) / 0.9

		const getMaxSpeedWithAeroDynamicEffect = (maxKph: number, handling: Handling) => {
			const baseDrag = handling.finitialDragCoeff
			const drag = userSettings.aerodynamicProfile ?? baseDrag
			const DRAG_MIN = 10
			const DRAG_MAX = 120
			const EFFECT_RANGE = 0.3
			const normalizedDrag = (drag - DRAG_MIN) / (DRAG_MAX - DRAG_MIN)
			const normalizedBase = (baseDrag - DRAG_MIN) / (DRAG_MAX - DRAG_MIN)
			const ratio = 1 + (normalizedBase - normalizedDrag) * EFFECT_RANGE
			return maxKph * ratio
		}

		const finalMaxSpeed = getMaxSpeedWithAeroDynamicEffect(theoreticalMaxKph, this.handling)

		return Number(finalMaxSpeed.toFixed(0))
	}

	private calculateDownForce = (userSettings: FineTuningSettingsProfileCache): number => {
		if (!this.handling || !userSettings) return 0
		const speedMs = this.handling.finitialDriveMaxFlatVel
		const baseDrag = this.handling.finitialDragCoeff
		const tunedDrag = userSettings.aerodynamicProfile ?? baseDrag
		const K_AERO = 0.01
		return Math.round(K_AERO * tunedDrag * speedMs * speedMs)
	}

	private calculateCastor = (userSettings: FineTuningSettingsProfileCache): number => {
		if (!this.handling || !userSettings) return 0
		return userSettings.castor ?? 0
	}

	private calculatePerformance = (userSettings: FineTuningSettingsProfileCache): number => {
		if (!this.baseParameters) return 0
		const offset = this.getDownpipeOffset(userSettings)
		const swing = offset > 0
			? this.Constants.DOWNPIPE_MAX_SWING
			: this.Constants.DOWNPIPE_MAX_SWING * 0.5
		return Math.round(this.baseParameters.power * (1 + swing * offset))
	}

	private calculateTorque = (userSettings: FineTuningSettingsProfileCache): number => {
		if (!this.baseParameters) return 0
		const offset = this.getDownpipeOffset(userSettings)
		const swing = offset > 0
			? this.Constants.DOWNPIPE_MAX_SWING
			: this.Constants.DOWNPIPE_MAX_SWING * 0.5
		return Math.round(this.baseParameters.torque * (1 - swing * offset))
	}

	private calculateAccelerationResponsiveness = (
		userSettings: FineTuningSettingsProfileCache
	): number => {
		if (!this.handling) return 0
		const MAX_RESPONSE_BONUS_MS = 25
		const MAX_RESPONSE_PENALTY_MS = 45

		const getBaseThrottleResponseMs = (handling: Handling): number => {
			const normalized =
				(handling.fdriveInertia - this.Constants.F_INERTIA_MIN) /
				(this.Constants.F_INERTIA_MAX - this.Constants.F_INERTIA_MIN)

			const clamped = Math.max(0, Math.min(1, normalized))

			return Math.round(
				this.Constants.ACCELERATION_RESPONSE_SLOWEST_MS -
				clamped * (this.Constants.ACCELERATION_RESPONSE_SLOWEST_MS - this.Constants.ACCELERATION_RESPONSE_FASTEST_MS)
			)
		}

		const getDownpipeThrottleDeltaMs = (
			userSettings: FineTuningSettingsProfileCache
		): number => {
			if (!this.handling) return 0

			const baseDownpipeMm = this.getBaseDownpipeMm(this.handling)
			const selectedDownpipeMm = userSettings.downpipe ?? baseDownpipeMm

			const baseNormalized =
				(baseDownpipeMm - this.Constants.DOWNPIPE_MIN_MM) /
				(this.Constants.DOWNPIPE_MAX_MM - this.Constants.DOWNPIPE_MIN_MM)

			const selectedNormalized =
				(selectedDownpipeMm - this.Constants.DOWNPIPE_MIN_MM) /
				(this.Constants.DOWNPIPE_MAX_MM - this.Constants.DOWNPIPE_MIN_MM)

			const delta = selectedNormalized - baseNormalized
			if (delta > 0) {
				return Math.round(delta * MAX_RESPONSE_PENALTY_MS)
			}
			if (delta < 0) {
				return -Math.round(Math.abs(delta) * MAX_RESPONSE_BONUS_MS)
			}

			return 0
		}

		const baseResponseMs = getBaseThrottleResponseMs(this.handling)
		const downpipeDeltaMs = getDownpipeThrottleDeltaMs(userSettings)

		return Math.max(50, Math.round(baseResponseMs + downpipeDeltaMs))
	}

	/***
	 * Utilities
	 */

	private getBaseDownpipeMm = (handling: Handling): number => {

		const normalized = (handling.finitialDriveForce - this.Constants.F_INTIAL_DRIVE_FORCE_MIN) / (this.Constants.F_INTIAL_DRIVE_FORCE_MAX - this.Constants.F_INTIAL_DRIVE_FORCE_MIN)
		const clamped = Math.max(0, Math.min(1, normalized))

		return this.Constants.DOWNPIPE_MIN_MM + clamped * (this.Constants.DOWNPIPE_MAX_MM - this.Constants.DOWNPIPE_MIN_MM)
	}

	private getDownpipeOffset = (userSettings: FineTuningSettingsProfileCache): number => {
		if (!this.handling) return 0

		const baseMm = this.getBaseDownpipeMm(this.handling)
		const selectedMm = userSettings.downpipe ?? baseMm
		const clampedMm = Math.max(this.Constants.DOWNPIPE_MIN_MM, Math.min(this.Constants.DOWNPIPE_MAX_MM, selectedMm))

		const difference = clampedMm - baseMm
		if (difference === 0) return 0

		const headroom = difference > 0
			? this.Constants.DOWNPIPE_MAX_MM - baseMm
			: baseMm - this.Constants.DOWNPIPE_MIN_MM

		return headroom > 0 ? difference / headroom : 0
	}

}
